<template>
    <div>
        <Transition v-if="isLoading"
                    name="spinner">
            <div id="cover-spin">
                <span class="loader">
                    <span>{{ $spinnerText }}</span>
                </span>
            </div>
        </Transition>

        <v-view :tableData="tableData"
                :tableProps="tableProps"
                :modalProps="modalProps"
                :columns="columns"
                :routes="routes"
                :initialSort="initialSort"
                :rowButtonAction="rowButtonAction"
                :showModeOpenId="showModeOpenId"
                :viewParams="viewParams"
                :buttonsVisibility="buttonsVisibility"
                v-model:selectedRows="selectedRows">

            <template #crud="formProps">
                <OfferForm :formProps="formProps">
                </OfferForm>
            </template>
            <template #search="searchProps">
                <OfferSearch :searchProps="searchProps">
                </OfferSearch>
            </template>

            <template #buttons="formProps">
                <BackButton v-if="isBackButtonVisible"
                            routeName="opportunity-index"
                            :customData="{ 'id': viewParams.opportunity_id }" />

                <span style="margin-right:25px"></span>
                <CustomButton name="createQuotation"
                              @click.stop="createQuotation(formProps)"
                              :className="buttonClass('green')">
                    <template class="flex items-center gap-1"><i class="fa-solid fa-list-check"></i><span>{{ __('Create quotation') }}</span></template>
                </CustomButton>

            </template>
        </v-view>

        <Dialog modal
                :visible="isCreateQuotationModalOpen"
                v-model:visible="isCreateQuotationModalOpen"
                :header="__('Create quotation')"
                :style="{ width: '800px' }"
                :pt="dialogPt">
            <p style="font-size:16px; font-weight:bold;color:cadetblue">{{ __('Quotation will be created for selected offers') }}</p>

            <CreateQuotationForm :selectedRows="selectedRows"
                                 :formProps="formPropsToPass"
                                 v-model:isCreateQuotationModalOpen="isCreateQuotationModalOpen">
            </CreateQuotationForm>
        </Dialog>

        <ConfirmDialog header="Confirm operation"
                       question="Do you want to copy offer?"
                       v-model="isConfirmationOpen"
                       :operation="copyOffer" />


    </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import OfferForm from './OfferForm.vue'
import OfferSearch from './OfferSearch.vue'
import BackButton from '@crud/BackButton.vue'
import CustomButton from '@crud/CustomButton.vue'
import ConfirmDialog from '@pages/Crud/ConfirmDialog.vue'
import Dialog from 'primevue/dialog';
import CreateQuotationForm from './CreateQuotationForm.vue'
import { __, getFromUrl, screenMessage, buttonClass, getFromRoute, printReport } from '@pages/Helper/utils.js';

const props = defineProps({
    tableData: Object,
    tableProps: Object,
    modalProps: Object,
    columns: Object,
    routes: Object,
    initialSort: Object,
    viewParams: Object,
    buttonsVisibility: Object,
})

const selectedRows = ref([])
const showModeOpenId = getFromUrl()
const isBackButtonVisible = ref(false)
const isCreateQuotationModalOpen = ref(false)
const formPropsToPass = ref(null)
const isLoading = ref(false)
const isConfirmationOpen = ref(false)
const getLazyData = ref(null)
const offerId = ref(null)
const dialogPt = {
    header: { class: 'form-header' },
    content: { class: 'form-body' },
};

//------------------------------------------------------------------------------

onMounted(() => {
    if (props.viewParams.opportunity_id) {
        isBackButtonVisible.value = true
    }
})

//------------------------------------------------------------------------------

function createQuotation(formProps) {
    if (!Array.isArray(selectedRows.value) || selectedRows.value.length === 0) {
        const message = {
            message: "No offers has been selected",
            title: "Offer selection error",
            type: "error",
            position: "center",
            timeout: 5,
        };
        screenMessage(message);
        return false;
    }
    const duplicate = selectedRows.value.find((offer, index, self) =>
        index !== self.findIndex((o) => o.assortment_id === offer.assortment_id)
    );
    if (duplicate) {
        const duplicatedName = duplicate.assortment_name;
        const message = {
            message: __("Assortment :assortment was selected more than once. You cannot create a quotation from offers with duplicate assortments", { assortment: "<span style='color:red;'>" + duplicatedName + "</span>" }),
            title: "Duplicate assortments error",
            type: "error",
            position: "center",
            timeout: 5,
        };
        screenMessage(message);
        return false;
    }

    isCreateQuotationModalOpen.value = true
    formPropsToPass.value = formProps;
}

//------------------------------------------------------------------------------

const rowButtonAction = async function (item, rowData, rowButtonFunctions, refreshData) {
    isLoading.value = true;

    switch (item.id) {
        case 'offer-print':
            const response = await getFromRoute('offer-print', { 'offer_id': rowData.id }, 'post')
            printReport(response)
            break;

        case 'offer-copy':
            isConfirmationOpen.value = true
            offerId.value = rowData.id
            getLazyData.value = refreshData
            break;

        default:
            console.log('Unknown action:', item.name);
    }

    isLoading.value = false;
}

//------------------------------------------------------------------------------

const copyOffer = async () => {
    const copyResponse = await getFromRoute('offer-copy', { 'offer_id': offerId.value }, 'post')
    if (copyResponse.success) {
        const message = {
            message: copyResponse.message,
            title: copyResponse.title,
            type: "success",
            position: "center",
            timeout: 5,
        };
        screenMessage(message);
        if (getLazyData.value) getLazyData.value();
    } else {
        const message = {
            message: copyResponse.message,
            title: copyResponse.title,
            type: "error",
            position: "center",
            timeout: 5,
        };
        screenMessage(message);
    }
}

//------------------------------------------------------------------------------

</script>

<style lang="scss" scoped></style>
